import { useEffect, useState } from 'react';
import {
  FileText,
  ExternalLink,
  Loader2,
  RotateCcw,
  CheckCircle2,
  AlertCircle,
  ChevronDown,
  Sparkles,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { domainApi } from '@/lib/api';

interface LlmsTxtSettingsProps {
  domainId: string;
  isVerified: boolean;
}

type LlmsMode = 'manual' | 'auto' | 'off';
type LlmsTab = 'llms' | 'full';

const MAX_BYTES = 100 * 1024;
const MAX_FULL_BYTES = 500 * 1024;

export function LlmsTxtSettings({ domainId, isVerified }: LlmsTxtSettingsProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [isChangingMode, setIsChangingMode] = useState(false);

  // llms.txt state (existing)
  const [content, setContent] = useState('');
  const [defaultContent, setDefaultContent] = useState('');
  const [isCustom, setIsCustom] = useState(false);
  const [publishedUrl, setPublishedUrl] = useState('');
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);

  // llms-full.txt state (new)
  const [fullContent, setFullContent] = useState('');
  const [fullDefault, setFullDefault] = useState('');
  const [fullIsCustom, setFullIsCustom] = useState(false);
  const [fullPublishedUrl, setFullPublishedUrl] = useState('');

  // Mode + auto state (new)
  const [mode, setMode] = useState<LlmsMode>('manual');
  const [autoAt, setAutoAt] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<LlmsTab>('llms');

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const load = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const [llmsRes, fullRes] = await Promise.all([
        domainApi.getLlmsTxt(domainId),
        domainApi.getLlmsFullTxt(domainId),
      ]);
      setContent(llmsRes.llmsTxt.content);
      setIsCustom(llmsRes.llmsTxt.isCustom);
      setPublishedUrl(llmsRes.llmsTxt.publishedUrl);
      setUpdatedAt(llmsRes.llmsTxt.updatedAt);
      setMode(llmsRes.llmsTxt.mode);
      setAutoAt(llmsRes.llmsTxt.autoAt);
      if (!llmsRes.llmsTxt.isCustom) setDefaultContent(llmsRes.llmsTxt.content);

      setFullContent(fullRes.llmsFullTxt.content);
      setFullIsCustom(fullRes.llmsFullTxt.isCustom);
      setFullPublishedUrl(fullRes.llmsFullTxt.publishedUrl);
      if (!fullRes.llmsFullTxt.isCustom) setFullDefault(fullRes.llmsFullTxt.content);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load llms.txt');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isVerified) {
      load();
    } else {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domainId, isVerified]);

  const isFull = activeTab === 'full';
  const activeContent = isFull ? fullContent : content;
  const activeCap = isFull ? MAX_FULL_BYTES : MAX_BYTES;
  const activeBytes = new Blob([activeContent]).size;
  const overLimit = activeBytes > activeCap;
  const activeIsCustom = isFull ? fullIsCustom : isCustom;
  const activePublishedUrl = isFull ? fullPublishedUrl : publishedUrl;

  const handleSave = async () => {
    if (overLimit) return;
    setIsSaving(true);
    setError(null);
    setSuccess(null);
    try {
      if (isFull) {
        const res = await domainApi.updateLlmsFullTxt(domainId, fullContent);
        setFullIsCustom(res.llmsFullTxt.isCustom);
        setSuccess(res.llmsFullTxt.isCustom ? 'Custom llms-full.txt saved.' : 'Reverted to default llms-full.txt.');
      } else {
        const res = await domainApi.updateLlmsTxt(domainId, content);
        setIsCustom(res.llmsTxt.isCustom);
        setSuccess(res.llmsTxt.isCustom ? 'Custom llms.txt saved.' : 'Reverted to default llms.txt.');
      }
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = async () => {
    const label = isFull ? 'llms-full.txt' : 'llms.txt';
    if (!confirm(`Reset ${label} to the auto-generated default? Your custom content will be replaced.`)) {
      return;
    }
    setIsResetting(true);
    setError(null);
    setSuccess(null);
    try {
      if (isFull) {
        await domainApi.resetLlmsFullTxt(domainId);
      } else {
        await domainApi.resetLlmsTxt(domainId);
      }
      setSuccess(`Reverted to default ${label}.`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Reset failed');
    } finally {
      setIsResetting(false);
    }
  };

  const handleModeChange = async (next: LlmsMode) => {
    if (next === mode) return;
    setIsChangingMode(true);
    setError(null);
    setSuccess(null);
    try {
      await domainApi.setLlmsTxtMode(domainId, next);
      setMode(next);
      setSuccess(`Mode set to ${next}.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Mode change failed');
    } finally {
      setIsChangingMode(false);
    }
  };

  const handleRegenerate = async () => {
    setIsRegenerating(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await domainApi.regenerateLlmsTxt(domainId);
      setSuccess(
        `Regenerated from sitemap: ${res.stats.urlsConsidered.toLocaleString()} URLs · llms.txt ${res.stats.llmsTxtBytes} B · llms-full.txt ${res.stats.llmsFullTxtBytes} B.`
      );
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Regenerate failed');
    } finally {
      setIsRegenerating(false);
    }
  };

  if (!isVerified) {
    return null;
  }

  return (
    <div className="bg-white rounded-xl border shadow-sm">
      <button
        type="button"
        onClick={() => setIsExpanded((v) => !v)}
        className="w-full flex items-center justify-between p-4 border-b hover:bg-slate-50"
      >
        <div className="flex items-center gap-3">
          <FileText className="w-5 h-5 text-indigo-600" />
          <div className="text-left">
            <h2 className="font-semibold">llms.txt for AI engines</h2>
            <p className="text-sm text-slate-500">
              {isCustom ? 'Custom content published' : 'Default content published'} at{' '}
              <code className="text-xs">/llms.txt</code> and{' '}
              <code className="text-xs">/llms-full.txt</code>
            </p>
          </div>
        </div>
        <ChevronDown className={cn('w-5 h-5 transition-transform', isExpanded && 'rotate-180')} />
      </button>

      {isExpanded && (
        <div className="p-4 space-y-4">
          {isLoading ? (
            <div className="flex items-center gap-2 text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading…
            </div>
          ) : (
            <>
              <p className="text-sm text-slate-600">
                We co-host <code>llms.txt</code> and <code>llms-full.txt</code> alongside your XML sitemap.
                The format follows the{' '}
                <a
                  href="https://llmstxt.org/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-600 hover:underline inline-flex items-center gap-0.5"
                >
                  llmstxt.org spec
                  <ExternalLink className="w-3 h-3" />
                </a>
                . llms.txt is a curated overview; llms-full.txt is the extended URL list.
              </p>

              {/* Mode selector */}
              <div className="flex flex-wrap items-center gap-2 p-3 bg-slate-50 rounded-lg border">
                <span className="text-sm font-medium text-slate-700">Mode:</span>
                {(['manual', 'auto', 'off'] as const).map((m) => (
                  <button
                    key={m}
                    type="button"
                    disabled={isChangingMode}
                    onClick={() => handleModeChange(m)}
                    className={cn(
                      'inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium border transition-colors',
                      mode === m
                        ? 'bg-indigo-600 text-white border-indigo-600'
                        : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-100',
                      isChangingMode && 'opacity-60 cursor-not-allowed'
                    )}
                  >
                    {m === 'auto' && <Sparkles className="w-3.5 h-3.5" />}
                    {m === 'manual' ? 'Manual' : m === 'auto' ? 'Auto' : 'Off'}
                  </button>
                ))}
                {mode === 'auto' && (
                  <button
                    type="button"
                    onClick={handleRegenerate}
                    disabled={isRegenerating}
                    className={cn(
                      'inline-flex items-center gap-1 ml-auto px-3 py-1.5 rounded-md text-sm font-medium border transition-colors',
                      isRegenerating
                        ? 'bg-slate-100 text-slate-400 cursor-not-allowed border-slate-200'
                        : 'bg-white text-indigo-600 border-indigo-300 hover:bg-indigo-50'
                    )}
                  >
                    {isRegenerating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                    Regenerate from sitemap
                  </button>
                )}
              </div>
              <p className="text-xs text-slate-500 -mt-2">
                {mode === 'manual' && 'Manual: edit and save your own content below.'}
                {mode === 'auto' && (
                  <>
                    Auto: content derived from your sitemap. Re-edit to switch back to manual.
                    {autoAt && (
                      <> · last regenerated {new Date(autoAt).toLocaleString()}</>
                    )}
                  </>
                )}
                {mode === 'off' && 'Off: a minimal placeholder is published. No LLM-readable summary.'}
              </p>

              {/* File tabs */}
              <div className="flex border-b border-slate-200">
                <button
                  type="button"
                  onClick={() => setActiveTab('llms')}
                  className={cn(
                    'px-4 py-2 text-sm font-medium border-b-2 -mb-px',
                    activeTab === 'llms'
                      ? 'border-indigo-600 text-indigo-600'
                      : 'border-transparent text-slate-500 hover:text-slate-700'
                  )}
                >
                  llms.txt
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('full')}
                  className={cn(
                    'px-4 py-2 text-sm font-medium border-b-2 -mb-px',
                    activeTab === 'full'
                      ? 'border-indigo-600 text-indigo-600'
                      : 'border-transparent text-slate-500 hover:text-slate-700'
                  )}
                >
                  llms-full.txt
                </button>
              </div>

              <textarea
                value={activeContent}
                onChange={(e) => {
                  if (isFull) setFullContent(e.target.value);
                  else setContent(e.target.value);
                  setSuccess(null);
                }}
                disabled={mode === 'off'}
                className={cn(
                  'w-full h-64 px-3 py-2 border rounded-lg font-mono text-sm focus:outline-none focus:ring-2',
                  overLimit
                    ? 'border-red-300 focus:ring-red-200'
                    : 'border-slate-200 focus:ring-indigo-200',
                  mode === 'off' && 'opacity-60 cursor-not-allowed bg-slate-50'
                )}
                placeholder={
                  isFull
                    ? '# Your site\n\n> Extended overview\n\n## All URLs\n\n- https://...'
                    : '# Your site\n\n> One-line summary\n\n## Section\n\n- [Title](https://...): description'
                }
                spellCheck={false}
              />

              <div className="flex items-center justify-between text-xs">
                <span className={cn('text-slate-500', overLimit && 'text-red-600 font-medium')}>
                  {activeBytes.toLocaleString()} / {activeCap.toLocaleString()} bytes
                </span>
                <span className="text-slate-500">
                  {activeIsCustom ? 'Custom content' : 'Showing generated default'}
                  {!isFull && updatedAt ? ` · updated ${new Date(updatedAt).toLocaleString()}` : ''}
                </span>
              </div>

              {error && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}
              {success && (
                <div className="flex items-start gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{success}</span>
                </div>
              )}

              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={isSaving || overLimit || mode === 'off'}
                  className={cn(
                    'inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                    overLimit || isSaving || mode === 'off'
                      ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                      : 'bg-indigo-600 text-white hover:bg-indigo-700'
                  )}
                >
                  {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                  Save
                </button>
                <button
                  type="button"
                  onClick={handleReset}
                  disabled={isResetting || !activeIsCustom}
                  className={cn(
                    'inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition-colors',
                    !activeIsCustom || isResetting
                      ? 'border-slate-200 text-slate-400 cursor-not-allowed'
                      : 'border-slate-300 text-slate-700 hover:bg-slate-50'
                  )}
                  title={!activeIsCustom ? 'Already on default' : 'Revert to the auto-generated default'}
                >
                  {isResetting ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                  Reset to default
                </button>
                <a
                  href={activePublishedUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium border border-slate-300 text-slate-700 hover:bg-slate-50"
                >
                  Open published
                  <ExternalLink className="w-3 h-3" />
                </a>
                {!activeIsCustom &&
                  ((isFull && fullDefault && fullContent !== fullDefault) ||
                    (!isFull && defaultContent && content !== defaultContent)) && (
                    <button
                      type="button"
                      onClick={() =>
                        isFull ? setFullContent(fullDefault) : setContent(defaultContent)
                      }
                      className="text-xs text-slate-500 hover:underline ml-auto"
                    >
                      Discard local edits
                    </button>
                  )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
